import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MessageSquare, Share2, Image as ImageIcon, Smile, Paperclip } from 'lucide-react';
import { FeedPost } from './data';
import { useApp } from './store';
function Composer({
  onOpen
}: {
  onOpen: () => void;
}) {
  const {
    user
  } = useApp();
  return <div className="bg-white rounded-xl border border-navy-100 p-4" data-id="element-37">
      <div className="flex items-center gap-3" data-id="element-38">
        <img src={user.avatar} alt="" className="w-10 h-10 rounded-full object-cover" data-id="element-39" />
        <button type="button" onClick={onOpen} className="flex-1 text-left bg-navy-50 rounded-full px-4 py-2.5 text-sm text-navy-400 hover:bg-navy-100 transition-colors" data-id="element-40">
          Share an update with the company…
        </button>
      </div>
      <div className="flex items-center gap-1 mt-3" data-id="element-41">
        <button onClick={onOpen} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy-500 hover:bg-navy-50 rounded-lg" data-id="element-42">
          <ImageIcon className="w-4 h-4" data-id="element-43" /> Photo
        </button>
        <button onClick={onOpen} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy-500 hover:bg-navy-50 rounded-lg" data-id="element-44">
          <Paperclip className="w-4 h-4" data-id="element-45" /> File
        </button>
        <button onClick={onOpen} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy-500 hover:bg-navy-50 rounded-lg" data-id="element-46">
          <Smile className="w-4 h-4" data-id="element-47" /> Feeling
        </button>
        <button onClick={onOpen} className="ml-auto px-4 py-1.5 text-xs font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors uppercase tracking-wide" data-id="element-48">
          Post
        </button>
      </div>
    </div>;
}
export function PostCard({
  post,
  index = 0
}: {
  post: FeedPost;
  index?: number;
}) {
  const [liked, setLiked] = useState(false);
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    duration: 0.4,
    delay: index * 0.05
  }} className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-id="element-49">
      <div className="p-4" data-id="element-50">
        <div className="flex items-center gap-3 mb-3" data-id="element-51">
          <img src={post.avatar} alt={post.author} className="w-11 h-11 rounded-full object-cover" data-id="element-52" />
          <div className="min-w-0" data-id="element-53">
            <p className="text-sm font-bold text-navy-900" data-id="element-54">{post.author}</p>
            <p className="text-xs text-navy-400" data-id="element-55">
              {post.role} · {post.time}
            </p>
          </div>
          <span className="ml-auto text-[0.65rem] font-semibold uppercase tracking-wide text-navy-600 bg-navy-50 px-2 py-1 rounded-full" data-id="element-56">
            {post.team}
          </span>
        </div>
        <Link to={`/feed/${post.id}`} className="block" data-id="element-57">
          <p className="text-sm text-navy-800 leading-relaxed hover:text-navy-600 transition-colors" data-id="element-58">
            {post.body}
          </p>
        </Link>
      </div>
      {post.image && <Link to={`/feed/${post.id}`} data-id="element-59">
          <img src={post.image} alt="" className="w-full max-h-80 object-cover" data-id="element-60" />
        </Link>}
      <div className="flex items-center gap-1 px-4 py-2.5 border-t border-navy-50" data-id="element-61">
        <button onClick={() => setLiked(v => !v)} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${liked ? 'text-gold-600' : 'text-navy-500 hover:bg-navy-50'}`} data-id="element-62">
          <Heart className={`w-4 h-4 ${liked ? 'fill-gold-500 text-gold-500' : ''}`} data-id="element-63" />{' '}
          {post.likes + (liked ? 1 : 0)}
        </button>
        <Link to={`/feed/${post.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-navy-500 hover:bg-navy-50 rounded-lg" data-id="element-64">
          <MessageSquare className="w-4 h-4" data-id="element-65" /> {post.comments.length}
        </Link>
        <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-navy-500 hover:bg-navy-50 rounded-lg ml-auto" data-id="element-66">
          <Share2 className="w-4 h-4" data-id="element-67" /> Share
        </button>
      </div>
    </motion.article>;
}
export function ActivityFeed({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    posts,
    openComposer
  } = useApp();
  const visible = compact ? posts.slice(0, 2) : posts;
  return <section aria-label="Company feed" className="space-y-4" data-id="element-68">
      <Composer onOpen={openComposer} data-id="element-69" />
      {visible.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-70" />)}
    </section>;
}